import React from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import isEqual from 'lodash/isEqual';
import dayjs from '../../../dayjs/dayjs_timezone';
import Alert from '../../components/Alert';
import ActivityClassPageParent from '../ActivityClassPageParent';
import Spinner from '../../components/Spinner';
import {
  customSelectStyles,
  getLocation,
  isDefaultTitle,
  isDifferentTimezones,
  parseInt,
} from '../../utilities/helperUtility';
import { FULL_DATE_TIME_FORMAT, SHORT_DATE_FORMAT } from '../../constants';
import { getTimeFrameFromDate } from '../../utilities/timeUtility';
import Translation from '../../utilities/localizationUtility';
import CloseModalIcon from '../components/CloseModalIcon';
import { stringToBoolean } from '../../utilities/conversionUtility';
import UpdatedBookingPage from '../components/UpdatedBookingPage';
import PaymentOptions from '../components/PaymentOptions';
import {
  getFirstBookableDate,
  getColumns,
  checkVariantsAvailability,
  checkVariantsAvailabilityForQuantity,
} from './helpers';
import {
  getAvailableSlot,
  getTimeslot,
  parseAvailableDates,
  parseSelectOptions,
} from '../helpers';
import ConfirmationPage from '../components/ActivityConfirmationPage';
import { StyledGoNextMonthButton, StyledTimeZone, StyledSubmit } from './ActivityPage.style';
import BrandingString from '../components/BrandingString';

export default class ActivityPage extends ActivityClassPageParent {
  constructor(props) {
    super(props);

    this.state = {
      availableDates: [],
      currentMonth: null,
      hoverStart: null,
      hoverEnteredTo: null,
      isModalOpen: false,
      modalHeight: 100,
      selectOptions: [],
      width: window.innerWidth,
      timeslotLoading: false,
      messages: [],
    };

    this.handleNextMonthClick = this.handleNextMonthClick.bind(this);
    this.handleDayMouseEnter = this.handleDayMouseEnter.bind(this);
    this.handleGetValidationQuantityFn = this.handleGetValidationQuantityFn.bind(this);
    this.handleMonthChange = this.handleMonthChange.bind(this);
    this.handleMenuOpen = this.handleMenuOpen.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);

    this.onChangeStore = {};
  }

  componentDidMount() {
    const { exceededBookingsLimit, exceededTrialPlan, __$testCrutchForFrames } = this.props;

    this.resetVariantsQuantity();
    super.componentDidMount();
    window.addEventListener('resize', this.updateDimensions);

    if (!exceededBookingsLimit && !exceededTrialPlan) {
      this.recursivelyFetchFrames(new Date(), __$testCrutchForFrames);
    }
  }

  componentDidUpdate(oldProps) {
    const { currentMonth } = this.state;
    const {
      frames,
      selectedDate,
      selectedLocation,
      selectedResource,
      selectedTimeslot,
      timeZone,
      setting,
      orderActions: { setOrderDetails },
      exceededBookingsLimit,
      exceededTrialPlan,
    } = this.props;

    const {
      setting: prevSetting,
      selectedDate: prevSelectedDate,
      timeZone: prevTimeZone,
      selectedLocation: prevSelectedLocation,
      selectedResource: prevSelectedResource,
      frames: prevFrames,
    } = oldProps;
    const currentSelectedLocation = prevSelectedLocation || {};
    const nextSelectedLocation = selectedLocation || {};
    const currentSelectedResource = prevSelectedResource || {};
    const nextSelectedResource = selectedResource || {};

    if (
      !isEqual(nextSelectedLocation, currentSelectedLocation)
      || !isEqual(nextSelectedResource, currentSelectedResource)
    ) {
      if (!exceededBookingsLimit && !exceededTrialPlan) {
        const date = selectedTimeslot || currentMonth;
        const dayjsDate = date ? dayjs(date) : dayjs();

        this.setupByDate({ date: dayjsDate.tz(timeZone) });
      }

      setOrderDetails({ date: null, selectedTimeslot: null });
    }

    if (!isEqual(prevTimeZone, timeZone)) {
      this.updateTimeZone(selectedTimeslot, true);
    }

    if (
      !isEqual(setting, prevSetting)
      || !isEqual(frames, prevFrames)
      || selectedDate !== prevSelectedDate
      || prevTimeZone !== timeZone
    ) {
      const condition = frames.length > 0;
      const updState = {
        availableDates: [],
        selectOptions: [],
      };

      this.updateSetup(condition, updState);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  checkVariants() {
    const { orderActions: { setVariantsQuantity }, variants } = this.props;
    const minimumQuantity = this.minimumQuantity();

    if (variants.length === 1 && minimumQuantity) {
      setVariantsQuantity(`${variants[0].external_id}_quantity`, minimumQuantity);
    }
  }

  getCanBook() {
    const { available, frames, selectedDate, variants } = this.props;
    return selectedDate && available > 0 && frames.length && variants.length;
  }

  getDuration() {
    const { durations: { duration } } = this.props;
    return duration;
  }

  getExtraColumns() {
    const { capacityType, frames, selectedLocation, selectedTimeslot, variants } = this.props;
    const attrs = {};
    const t = new Translation(this.props);
    const locationId = selectedLocation ? selectedLocation.id : null;

    if (!variants) {
      return null;
    }

    variants.forEach((variant) => {
      attrs[variant.external_id] = this.onlyDefaultTitle() ? { style: { float: 'right' } } : {};

      if (selectedTimeslot) {
        let available = 0;

        const slot = frames
          .reduce((all, _frame) => all.concat(_frame.timeslots), [])
          .find((s) => (
            s.start === selectedTimeslot
              && (!(locationId && s.location_id) || s.location_id === locationId)
          ));

        if (slot) {
          const variantAvailability = slot.availability.find((ava) => ava.external_id === variant.external_id);
          const isAvailable = (variantAvailability ? variantAvailability.status : 0) === 1;

          if (isAvailable) {
            available = capacityType === 2 ? slot.resource_availability : variantAvailability.available;
          }
        }

        attrs[variant.external_id] = { ...attrs[variant.external_id], max: available };
      }
    });

    return [
      {
        attrs,
        header: t.key('columnQuantity'),
        type: 'number',
        onChange: this.getOnChange(attrs),
      },
    ];
  }

  getOnChange(attrs) {
    const cacheKey = JSON.stringify(attrs);

    if (this.onChangeStore[cacheKey]) {
      return this.onChangeStore[cacheKey];
    }

    const fn = (target, max) => {
      const { name, value } = target;
      const {
        available,
        capacityType,
        orderVariants,
        totalQuantity,
        orderActions: { setVariantsQuantity },
        variants,
        setting,
      } = this.props;
      const variantId = name.split('_')[0];
      const variant = orderVariants[variantId];
      let quantity = Math.abs(parseInt(value));
      quantity = max >= quantity ? quantity : max;

      if (variant && (quantity - variant.quantity >= 2)) {
        const newTotalQuantity = totalQuantity - variant.quantity + quantity;

        if (available < newTotalQuantity) {
          quantity = available - (totalQuantity - variant.quantity);
        }
      }

      if (checkVariantsAvailabilityForQuantity({
        attrs,
        variantId,
        quantity,
        available,
        capacityType,
        orderVariants,
        variants,
        setting,
      })) {
        if (this.spinner) {
          this.spinner.show();
        }

        setVariantsQuantity(name, quantity);
      }
    };

    this.onChangeStore[cacheKey] = fn;
    return fn;
  }

  getSubmitButtonLabel() {
    const { bookingToken, loading } = this.props;
    const t = new Translation(this.props);
    const canBook = this.getCanBook();
    let submitLabel = t.key('noAvailability');

    if (canBook) {
      submitLabel = t.key('nextButton');
    } else if (loading) {
      submitLabel = 'Loading...';
    }

    if (bookingToken) {
      submitLabel = t.key('reschedule') || 'Reschedule';
    }

    return submitLabel;
  }

  handleAddItems(reservation, callback) {
    const { orderVariants } = this.props;
    const sliceIndex = filter(orderVariants, (x) => !!x.quantity).length;
    const type = ['Date', 'Location'];

    this.onAddItems({
      reservation,
      checkAllDay: true,
      secondSliceParam: sliceIndex,
      type,
      callback,
    });
  }

  handleDayMouseEnter(day) {
    const { timeslotLoading } = this.state;

    if (timeslotLoading) {
      return;
    }

    const { timeZone, durations: { duration } } = this.props;
    const date = dayjs(day).format(SHORT_DATE_FORMAT);
    const options = parseSelectOptions(date, this.props);

    if (options.length > 0) {
      const newDate = dayjs(options[0].value).tz(timeZone).format(FULL_DATE_TIME_FORMAT);
      this.setState({
        hoverStart: dayjs(newDate).toDate(),
        hoverEnteredTo: dayjs(newDate).add(duration - 1, 'seconds').toDate(),
      });
    }
  }

  handleGetValidationQuantityFn(validationFn) {
    this.validateTotalQuantity = validationFn;
  }

  handleMenuOpen() {
    this.setTimeslotLoading(true);
  }

  handleMonthChange(value) {
    const { capacityType, orderActions: { setOrderDetails }, maxAvailability } = this.props;
    if (this.spinner) {
      this.spinner.show();
    }

    this.onMonthChange(value, (data) => {
      const { selectedDate } = this.props;
      const day = dayjs(value);
      const selected = selectedDate ? dayjs(selectedDate) : null;
      const availableDates = parseAvailableDates(this.props);
      let bookableDate = selected?.format(SHORT_DATE_FORMAT);

      if (!availableDates.includes(bookableDate) || !selected?.isSame(day, 'month')) {
        bookableDate = getFirstBookableDate(
          data.frames,
          availableDates,
          { month: day },
        );
      }

      const frame = data.frames.find((f) => f.date === bookableDate);
      const timeslot = getTimeslot(frame, day, true);

      if (timeslot) {
        setOrderDetails({
          date: frame.date,
          available: getAvailableSlot(timeslot, capacityType, maxAvailability),
          selectedTimeslot: timeslot.start,
        });
      } else {
        setOrderDetails({ date: null, selectedTimeslot: null });
      }
    });
  }

  handleNextMonthClick() {
    const { nextOccurrence } = this.props;
    const { currentMonth } = this.state;
    const nextMonth = dayjs(currentMonth).add(1, 'months');

    if (nextOccurrence) {
      this.recursivelyFetchFrames(nextMonth.format(SHORT_DATE_FORMAT), (occurrence, data) => {
        const { frames } = data;

        if (frames.length) {
          this.setState({
            currentMonth: dayjs(occurrence).format(SHORT_DATE_FORMAT),
          });
        }
      });
    }
  }

  handleSubmit(e) {
    const { orderVariants, orderActions: { submitOrder } } = this.props;
    const messages = this.validateTotalQuantity ? this.validateTotalQuantity() : [];

    this.onSubmit({
      element: e,
      messages,
      itemsResponse: orderVariants,
      action: submitOrder,
    });
  }

  minimumQuantity() {
    const { setting } = this.props;
    return stringToBoolean(setting.set_minimum_quantity) ? setting.minimum_quantity : 0;
  }

  onDayClick(day, modifiers = {}) {
    const res = super.onDayClick(day, modifiers);

    if (res === 'break') {
      return;
    }

    const newSelection = dayjs(day).format(SHORT_DATE_FORMAT);
    this.resetVariantsQuantity();
    const selectOptions = parseSelectOptions(newSelection, this.props);
    this.setState({ selectOptions });
  }

  onSelectChange(value) {
    const {
      orderActions: { setOrderDetails },
      frames,
      capacityType,
      selectedLocation,
      selectedDate,
      maxAvailability,
    } = this.props;

    if (value && selectedDate) {
      const frame = frames.find((x) => x.date === selectedDate);
      const locationId = selectedLocation ? selectedLocation.id : null;
      const timeslot = frame.timeslots.find((x) => (
        x.start === value
          && (!(locationId && x.location_id) || x.location_id === locationId)
      ));

      if (timeslot) {
        this.resetVariantsQuantity();
        setOrderDetails({
          date: frame.date,
          available: getAvailableSlot(timeslot, capacityType, maxAvailability),
          selectedTimeslot: value,
        });
      }
    }
  }

  onlyDefaultTitle() {
    const { variants } = this.props;
    return variants && variants.length === 1 && isDefaultTitle(variants[0].title);
  }

  recursivelyFetchFrames(occurrence, callback) {
    // logic of querying timeslot
    // first, we need 2 params: start and finish
    // second, dates must use server timezone to intialize and eventually sent in UTC
    // Start:
    // - should be in server timezone

    const self = this;
    const {
      setting,
      locations,
      rescheduleBookingLocation,
      selectedLocation,
      selectedResource,
      frameActions: { fetchFrames },
    } = this.props;
    const { timeZone } = window.bta;
    const { start, finish } = getTimeFrameFromDate(occurrence, timeZone);
    const location = isDifferentTimezones(locations) ? getLocation(locations, setting, {
      locationId: rescheduleBookingLocation,
      useDefault: true,
    }) : {};

    const month = dayjs(occurrence);
    const nextMonth = month.add(1, 'month');

    fetchFrames({
      start: start.format(),
      finish: finish.format(),
      location_id: selectedLocation?.id || location.id,
      resource_id: selectedResource?.id,
    }).then((framesData) => (
      new Promise((resolve) => {
        const { frames } = framesData;
        const framesInMonth = frames.filter((frame) => (dayjs(frame.date).isSame(month, 'month')));
        const framesInNextMonth = frames.filter((frame) => (dayjs(frame.date).isSame(nextMonth, 'month')));

        if (framesInMonth.length === 0) {
          const { nextOccurrence } = framesData;

          if (framesInNextMonth.length > 0) {
            self.recursivelyFetchFrames(nextMonth.format(SHORT_DATE_FORMAT), callback);
          } else if (nextOccurrence) {
            // Could get the empty data in first time, so use the nextOccurrence to do again
            self.recursivelyFetchFrames(nextOccurrence, callback);
          } else {
            resolve(framesData);
          }
        } else {
          if (callback) {
            callback(month, framesData);
          }

          resolve(framesData);
        }
      })
    ));
  }

  renderSubmitButton() {
    const { bookingToken } = this.props;
    const { messages } = this.state;
    const submitFunc = bookingToken ? this.handleSubmit : this.openConfirmationModal;
    const canBook = this.getCanBook();
    const submitLabel = this.getSubmitButtonLabel();

    return (
      <StyledSubmit
        label={submitLabel}
        messages={bookingToken ? messages : null}
        onSubmit={submitFunc}
        canBook={canBook}
      />
    );
  }

  renderTimeslotSelect() {
    const { selectOptions } = this.state;
    const {
      apiVerbose,
      loading,
      style: { select },
      selectedTimeslot,
    } = this.props;
    const t = new Translation(this.props);
    let isDisabled;
    let placeholder = '';

    if (selectOptions.length === 0) {
      isDisabled = true;
      placeholder = !loading && (t.key('noOptions') || 'No options');
    }

    return (
      <StyledGoNextMonthButton
        className="Select-dropup"
        backspaceRemovesValue={false}
        isClearable={false}
        isDisabled={isDisabled}
        isLoading={loading}
        isSearchable={false}
        menuPlacement="auto"
        options={selectOptions}
        onChange={this.onSelectChange}
        onMenuOpen={this.handleMenuOpen}
        onMenuClose={this.setTimeslotLoading}
        placeholder={placeholder}
        buildLabel={apiVerbose}
        styles={customSelectStyles(select)}
        value={selectedTimeslot}
      />
    );
  }

  resetVariantsQuantity() {
    const {
      bookingToken,
      hasSeasonalRates,
      orderActions: { resetVariantsQuantity, setVariantsQuantity },
      rescheduleBookingVariants,
    } = this.props;

    if (!!bookingToken && rescheduleBookingVariants.length > 0) {
      rescheduleBookingVariants.forEach((variant) => (
        setVariantsQuantity(`${variant.variant_id}_quantity`, variant.quantity)
      ));
    } else if (hasSeasonalRates) {
      resetVariantsQuantity();
    } else {
      this.resetQuantity();
    }
  }

  resetQuantity() {
    const { orderActions: { setVariantsQuantity }, variants } = this.props;
    const quantity = variants.length === 1 ? 1 : 0;
    variants.forEach((variant) => { setVariantsQuantity(`${variant.external_id}_quantity`, quantity); });
  }

  setDefaultSetup() {
    const { currentMonth } = this.state;
    const {
      frames,
      orderActions: { setOrderDetails },
      selectedDate,
      selectedLocation,
      timeZone,
    } = this.props;
    const availableDates = parseAvailableDates({ frames, selectedLocation, timeZone });

    if (!selectedDate && !currentMonth) {
      const firstBookableDate = getFirstBookableDate(frames, availableDates);

      if (firstBookableDate) {
        setOrderDetails({ date: firstBookableDate });
        this.setDefaultDates(firstBookableDate, availableDates, true);
      }
    } else {
      this.setState({
        availableDates,
        selectOptions: parseSelectOptions(selectedDate, this.props),
      });
    }
  }

  render() {
    if (this.state.bookingUpdated) {
      return <UpdatedBookingPage />;
    }

    const { modalHeight, messages } = this.state;
    const {
      ability,
      allDay,
      available,
      capacityType,
      hasSeasonalRates,
      orderActions,
      orderVariants,
      region,
      selectedTimeslot,
      setting,
      style,
      timeZone,
      totalQuantity,
      shopDatepickerTimeInterval,
      shopDateFormat,
      brandingVisualized,
    } = this.props;
    const showBookingFields = stringToBoolean(setting.show_booking_fields);
    const showWaitlist = stringToBoolean(setting.allow_waitlist) && ability.isStandard;
    const columns = getColumns(this.props);
    const extraColumns = this.getExtraColumns();
    const showGoNextMonthButton = this.getShowNextMoBtn();
    const variantsAvailability = checkVariantsAvailability(
      orderVariants,
      extraColumns[0].attrs,
      available,
      capacityType,
    );
    const canBook = this.getCanBook();
    const t = new Translation(this.props);
    const isRenderWaitlistButton = showWaitlist && selectedTimeslot && !canBook;
    const wrapperStyle = { ...style.body, minHeight: '420px', ...style.boxShadow };

    return (
      <div className="text-center center widget-container" style={{ minHeight: modalHeight + 20 }}>
        <CloseModalIcon />
        <Alert ref={this.setAlertRef} />
        <div className={`sub-panel ${setting.mode}`} style={wrapperStyle}>
          <h3 className="text-center widget-title" style={{ ...style.title }}>
              {t.key('title')}
          </h3>

          {this.renderLocationDropdown()}
          {this.renderResourceDropdawn()}
          {this.renderExceededAlert(true)}

          <PaymentOptions />

          <div className="relative date-picker-container text-center">
            <Spinner ref={(ref) => (this.spinner = ref)} />

            {this.renderDatePicker(true)}
            {showGoNextMonthButton ? this.renderGoNextMonthButton() : this.renderTimeslotSelect()}

            <StyledTimeZone timeZone={timeZone} selectedTime={selectedTimeslot} />
          </div>

          {this.renderWaitlist(selectedTimeslot, { needActivity: true })}
          {isRenderWaitlistButton ? this.renderWaitlistButton() : this.renderSubmitButton()}

          {this.renderModal(
            <div className="text-center center">
              <ConfirmationPage
                allDay={allDay}
                canBook={canBook}
                capacityType={capacityType}
                closeModal={this.closeModal ? this.closeModal : () => {}}
                columns={columns}
                dateFormat={shopDateFormat}
                enabledBookingFields={showBookingFields}
                extraColumns={extraColumns}
                getValidationFn={this.handleGetValidationFn}
                getValidationQuantityFn={this.handleGetValidationQuantityFn}
                hasSeasonalRates={hasSeasonalRates}
                hideHead={this.onlyDefaultTitle()}
                messages={messages}
                minimumQuantity={this.minimumQuantity()}
                onSubmit={this.handleSubmit}
                orderActions={orderActions}
                region={region}
                setting={setting}
                setWrapperHeight={this.setWrapperHeight}
                style={style}
                timeInterval={shopDatepickerTimeInterval}
                timeZone={timeZone}
                totalQuantity={totalQuantity}
                translation={t}
                variants={orderVariants}
                selectedTimeslot={selectedTimeslot}
                variantsAvailability={variantsAvailability}
              />
            </div>,
            brandingVisualized
          )}
          {brandingVisualized && <BrandingString />}
        </div>
      </div>
    );
  }
}

ActivityPage.propTypes = {
  timeZone: PropTypes.string,
  productOptions: PropTypes.arrayOf(PropTypes.shape({
    position: PropTypes.number,
    name: PropTypes.string,
    values: PropTypes.array,
  })),
  variants: PropTypes.array,
  available: PropTypes.number,
  bookingFields: PropTypes.array,
  additionalDetails: PropTypes.object,
  frames: PropTypes.array,
  totalQuantity: PropTypes.number,
  nextOccurrence: PropTypes.string,
  lastOccurrence: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  selectedDate: PropTypes.string,
  selectedTimeslot: PropTypes.string,
  orderActions: PropTypes.shape({
    setVariantsQuantity: PropTypes.func,
    setSelectedTimeslot: PropTypes.func,
    setOrderDetails: PropTypes.func,
    changeAdditionalDetails: PropTypes.func,
  }),
  cartActions: PropTypes.shape({
    addItems: PropTypes.func,
  }),
  frameActions: PropTypes.shape({ fetchFrames: PropTypes.func }),
  orderVariants: PropTypes.shape({
    external_id: PropTypes.string,
    quantity: PropTypes.number,
    start: PropTypes.instanceOf(Date),
  }),
  style: PropTypes.object,
};
